import React, { useEffect, useState } from "react";
import {
  PageAction,
  PageTemplate,
} from "@components/pageTemplate/PageTemplate";
import {
  CreatableSelect,
  CreatableSelectOption,
} from "@components/creatableSelect/CreatableSelect";
import { SoilTypeService } from "@modules/soil-type/soil-type.service";
import { useInjection } from "@di/DependencyContext";
import { DependencyType } from "@di/DependencyType";
import { CropTypeService } from "@modules/crop-type/crop-type.service";
import { CoverCropService } from "@modules/cover-crop/cover-crop.service";
import { CultivationService } from "@modules/cultivation/cultivation.service";
import { CropService } from "@modules/crop/crop.service";
import {
  CreateLabResultInput,
  CreateSoilSampleInput,
  LabIndicatorDefinition,
  Laboratory,
  MoistureLevel,
  Season,
  SoilSample,
  UpdateLabResultInput,
  UpdateSoilSampleInput,
} from "generated/graphql";
import { useNavigate, useParams } from "react-router-dom";
import { SoilSampleService } from "@modules/soil-sample/soil-sample.service";
import { PartialDeep } from "type-fest";
import { LoadingState } from "@util/loadingstate.util";
import { toast } from "sonner";
import { parseServerError } from "@util/parseError";
import Loading from "@components/loading/Loading";
import { LabResultField } from "@pages/soil-samples/lab-result/LabResultField";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Label } from "~/components/ui/label";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Checkbox } from "~/components/ui/checkbox";
import { Button } from "~/components/ui/button";
import PlusIcon from "../../icons/plus-circle.svg?react";
import { LaboratoryService } from "@modules/laboratory/laboratory.service";
import { CreateDefinition } from "@pages/soil-samples/lab-result/CreateDefinition";
import { LabResultService } from "@modules/lab-result/lab-result-service";
import { getRoute, Routes } from "~/routes";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { v1 } from "uuid";
import { LabIndicatorDefinitionService } from "@modules/lab-indicator-definition/lab-indicator-definition.service";

type EntityType =
  | "SoilType"
  | "CropType"
  | "Crop"
  | "Cultivation"
  | "CoverCrop"
  | "Laboratory"
  | "LabIndicatorDefinition";

export interface EditSoilSampleProps {
  mode: "create" | "edit";
}

export const EditSoilSample: React.FC<EditSoilSampleProps> = (props) => {
  const [soilSample, setSoilSample] = useState<PartialDeep<SoilSample>>();
  const [soilTypes, setSoilTypes] = useState<CreatableSelectOption[]>();
  const [cropTypes, setCropTypes] = useState<CreatableSelectOption[]>();
  const [crops, setCrops] = useState<CreatableSelectOption[]>();
  const [cultivations, setCultivations] = useState<CreatableSelectOption[]>();
  const [coverCrops, setCoverCrops] = useState<CreatableSelectOption[]>();
  const [laboratories, setLaboratories] = useState<Laboratory[]>();
  const params = useParams<{ id: string }>();
  const [input, setInput] = useState<
    PartialDeep<UpdateSoilSampleInput> | PartialDeep<CreateSoilSampleInput>
  >();
  const [loadState, setLoadState] = useState<LoadingState>(
    LoadingState.Loading
  );
  const [updateLabResultInputs, setUpdateLabResultInputs] = useState<
    UpdateLabResultInput[]
  >([]);
  const [createLabResultInputs, setCreateLabResultInputs] = useState<
    Partial<CreateLabResultInput>[]
  >([]);
  const [labIndicatorDefinitions, setLabIndicatorDefinitions] = useState<
    Partial<LabIndicatorDefinition>[]
  >([]);
  const [key, setKey] = useState(v1()); // A horrid hack
  const navigate = useNavigate();
  const [parent] = useAutoAnimate(/* optional config */);

  const soilTypeService = useInjection<SoilTypeService>(
    DependencyType.SoilTypeService
  );
  const cropTypeService = useInjection<CropTypeService>(
    DependencyType.CropTypeService
  );
  const cropService = useInjection<CropService>(DependencyType.CropService);
  const cultivationService = useInjection<CultivationService>(
    DependencyType.CultivationService
  );
  const coverCropService = useInjection<CoverCropService>(
    DependencyType.CoverCropService
  );
  const soilSampleService = useInjection<SoilSampleService>(
    DependencyType.SoilSampleService
  );
  const laboratoryService = useInjection<LaboratoryService>(
    DependencyType.LaboratoryService
  );
  const labResultService = useInjection<LabResultService>(
    DependencyType.LabResultService
  );
  const labIndicatorDefinitionService =
    useInjection<LabIndicatorDefinitionService>(
      DependencyType.LabIndicatorDefinitionService
    );

  const handleSave = async () => {
    try {
      setLoadState(LoadingState.Loading);
      if (props.mode === "create") {
        const created = await soilSampleService.create(
          input as CreateSoilSampleInput
        );
        if (created.id) {
          navigate(getRoute(Routes.SoilSampleDetails, { id: created.id }));
        }
      } else {
        // Update metadata
        const updateMetadata = soilSampleService.update(
          input as UpdateSoilSampleInput
        );
        // Update existing lab results
        const filteredUpdateInputs = updateLabResultInputs.filter(
          (input) =>
            input.value !== "" &&
            (input.value !==
              soilSample?.labResults?.nodes?.find((lr) => lr.id === input.id)?.value ||
              input.laboratoryId !==
                soilSample?.labResults?.nodes?.find((lr) => lr.id === input.id)
                  ?.laboratory?.id)
        );
        const updatePromises = filteredUpdateInputs.map((input) =>
          labResultService.update(input, soilSample?.id ?? "")
        );
        // Create required new lab results
        const filteredCreateInputs = createLabResultInputs
          .filter(
            (input) =>
              input.value !== "" &&
              input.indicatorId !== "" &&
              input.laboratoryId !== ""
          )
          .map((input) => ({
            ...input,
            soilSampleId: soilSample?.id ?? "",
          }));

        // Deduplicate create inputs by labid, indicatorid
        const dedupedCreateInputs = filteredCreateInputs.reduce(
          (acc, input) => {
            const existing = acc.find(
              (i) =>
                i.indicatorId === input.indicatorId &&
                i.laboratoryId === input.laboratoryId
            );
            if (existing) {
              return acc;
            }
            return [...acc, input];
          },
          [] as Partial<CreateLabResultInput>[]
        );

        const createPromises = dedupedCreateInputs.map((input) =>
          labResultService.create(input as CreateLabResultInput)
        );
        await Promise.all([
          updateMetadata,
          ...updatePromises,
          ...createPromises,
        ]);
      }
      toast.success("Soil sample saved successfully");
      setLoadState(LoadingState.Loaded);
    } catch (e) {
      const parsedError = parseServerError(e);
      toast.error(`Error saving soil sample: ${parsedError.message}`);
      setLoadState(LoadingState.Error);
    }
  };

  const fetchEntities = async (type: EntityType) => {
    if (type === "SoilType") {
      const soilTypes = await soilTypeService.findAll();
      setSoilTypes(soilTypes?.nodes?.map((st) => ({ value: st.id, label: st.name })) ?? []);
    } else if (type === "CropType") {
      const cropTypes = await cropTypeService.findAll();
      setCropTypes(cropTypes?.nodes?.map((ct) => ({ value: ct.id, label: ct.name })) ?? []);
    } else if (type === "Crop") {
      const crops = await cropService.findAll();
      setCrops(crops?.nodes?.map((c) => ({ value: c.id, label: c.name })) ?? []);
    } else if (type === "Cultivation") {
      const cultivations = await cultivationService.findAll();
      setCultivations(
        cultivations?.nodes?.map((c) => ({ value: c.id, label: c.name })) ?? []
      );
    } else if (type === "CoverCrop") {
      const coverCrops = await coverCropService.findAll();
      setCoverCrops(coverCrops?.nodes?.map((cc) => ({ value: cc.id, label: cc.name })) ?? []);
    } else if (type === "Laboratory") {
      const labs = await laboratoryService.findAll();
      setLaboratories((labs?.nodes ?? []) as Laboratory[]);
    } else if (type === "LabIndicatorDefinition") {
      const definitions = await labIndicatorDefinitionService.findAll();
      setLabIndicatorDefinitions(definitions?.nodes ?? []);
    } else {
      throw new Error("Invalid entity");
    }
  };

  useEffect(() => {
    void Promise.allSettled([
      fetchEntities("SoilType"),
      fetchEntities("CropType"),
      fetchEntities("Crop"),
      fetchEntities("Cultivation"),
      fetchEntities("CoverCrop"),
      fetchEntities("Laboratory"),
      fetchEntities("LabIndicatorDefinition"),
    ]).finally(() => setLoadState(LoadingState.Loaded));

    if (props.mode === "edit") {
      void (async () => {
        const soilSampleId = params.id?.trim();
        if (!soilSampleId) {
          toast.error("Missing soil sample ID.");
          setLoadState(LoadingState.Error);
          return;
        }

        const soilSample = await soilSampleService.findOne(soilSampleId);
        if (soilSample) {
          setSoilSample(soilSample);
          setInput({
            id: soilSample.id!,
            name: soilSample.name!,
            isReferenceZone: soilSample.isReferenceZone ?? false,
            moisture: soilSample.moisture ?? undefined,
            season: soilSample.season ?? undefined,
            sampleDate: soilSample.sampleDate ?? undefined,
            strawRemoved: soilSample.strawRemoved ?? false,
            fym: soilSample.fym ?? false,
            soilTypeId: soilSample.soilType?.id ?? undefined,
            cropTypeId: soilSample.cropType?.id ?? undefined,
            lastYearCropId: soilSample.lastYearCrop?.id ?? undefined,
            yearBeforeLastCropId: soilSample.yearBeforeLastCrop?.id ?? undefined,
            cultivationId: soilSample.cultivation?.id ?? undefined,
            coverCropId: soilSample.coverCrop?.id ?? undefined,
          });
        } else {
          console.error("Soil sample not found");
        }
      })();
    } else {
      setSoilSample({});
      const createInput: PartialDeep<CreateSoilSampleInput> = {
        name: "",
        fym: false,
        strawRemoved: false,
        isReferenceZone: false,
        sampleDate: undefined,
      };
      setInput(createInput);
    }
  }, [props.mode]);

  useEffect(() => {
    if (
      loadState === LoadingState.Loaded &&
      soilSample?.labResults &&
      labIndicatorDefinitions.length
    ) {
      const alwaysShowDefinitions = labIndicatorDefinitions.filter(
        (def) => def.alwaysShow
      );
      const notFilled = alwaysShowDefinitions.filter(
        (def) =>
          !soilSample?.labResults?.nodes?.find(
            (lr) => lr.indicator?.id === def.id
          )
      );
      const createInputs = notFilled.map((def) => ({
        indicatorId: def.id,
        laboratoryId: "",
        value: "",
        soilSampleId: soilSample?.id ?? "",
      }));
      // Add any createinputs that aren't already present
      setCreateLabResultInputs((prev) => [
        ...prev,
        ...createInputs.filter(
          (input) =>
            !prev.find(
              (prevInput) =>
                prevInput.indicatorId ===
                input.indicatorId
            )
        ),
      ]);
    }
  }, [loadState, soilSample, labIndicatorDefinitions]);

  const handleCreateOption = async (
    entity: EntityType,
    value: string
  ): Promise<CreatableSelectOption> => {
    if (entity === "SoilType") {
      const newSoilType = await soilTypeService.create({ name: value });
      if (newSoilType) {
        await fetchEntities("SoilType");
        setSoilSample({ ...soilSample, soilType: { id: newSoilType.id } });
        setInput({ ...input, soilTypeId: newSoilType.id });
        return { value: newSoilType.id, label: newSoilType.name };
      }
    } else if (entity === "CropType") {
      const newCropType = await cropTypeService.create({ name: value });
      if (newCropType) {
        await fetchEntities("CropType");
        setSoilSample({ ...soilSample, cropType: { id: newCropType.id } });
        setInput({ ...input, cropTypeId: newCropType.id });
        return { value: newCropType.id, label: newCropType.name };
      }
    } else if (entity === "Crop") {
      const newCrop = await cropService.create({ name: value });
      if (newCrop) {
        await fetchEntities("Crop");
        setSoilSample({ ...soilSample, lastYearCrop: { id: newCrop.id } });
        setInput({ ...input, lastYearCropId: newCrop.id });
        return { value: newCrop.id, label: newCrop.name };
      }
    } else if (entity === "Cultivation") {
      const newCultivation = await cultivationService.create({ name: value });
      if (newCultivation) {
        await fetchEntities("Cultivation");
        setSoilSample({
          ...soilSample,
          cultivation: { id: newCultivation.id },
        });
        setInput({ ...input, cultivationId: newCultivation.id });
        return { value: newCultivation.id, label: newCultivation.name };
      }
    } else if (entity === "CoverCrop") {
      const newCoverCrop = await coverCropService.create({ name: value });
      if (newCoverCrop) {
        await fetchEntities("CoverCrop");
        setSoilSample({ ...soilSample, coverCrop: { id: newCoverCrop.id } });
        setInput({ ...input, coverCropId: newCoverCrop.id });
        return { value: newCoverCrop.id, label: newCoverCrop.name };
      }
    }
    throw new Error("Invalid entity");
  };

  useEffect(() => {
    // A fix for this is backlogged, so need to hack for now
    setKey(v1());
  }, [props.mode, params.id]);

  return (
    <PageTemplate
      key={key}
      title={`${props.mode === "create" ? "Create" : "Edit"} Soil Sample`}
      subtitle={
        props.mode === "create"
          ? "Create a new soil sample"
          : soilSample
            ? `Last edited: ${(soilSample.updatedAt
                ? new Date(soilSample.updatedAt)
                : new Date()
              ).toLocaleString()}`
            : "Loading..."
      }
      breadcrumbs={[
        { label: "Home", path: Routes.Home },
        { label: "Soil Samples", path: Routes.SoilSamples },
        ...(props.mode === "edit"
          ? [
              {
                label: soilSample?.name ?? "Loading...",
                path: getRoute(Routes.SoilSampleDetails, {
                  id: soilSample?.id ?? "",
                }),
              },
            ]
          : [
              {
                label: "Create",
                path: getRoute(Routes.SoilSampleCreate),
              },
            ]),
      ]}
      activeItem="Soil Samples"
      actions={[
        ...(props.mode === "edit"
          ? [
              {
                text: "Create new sample",
                disabled: loadState === LoadingState.Loading,
                action: () => {
                  setSoilSample({});
                  setInput({
                    name: "",
                    fym: false,
                    strawRemoved: false,
                    isReferenceZone: false,
                    sampleDate: undefined,
                  });
                  navigate(getRoute(Routes.SoilSampleCreate));
                },
                variant: "secondary",
                className:
                  "bg-emerald-100 text-emerald-900 hover:bg-emerald-200",
              } as PageAction,
            ]
          : []),
        {
          text: "Save",
          disabled: loadState === LoadingState.Loading,
          action: handleSave,
        },
      ]}
    >
      <Card>
        <CardHeader>
          <CardTitle>Sample metadata</CardTitle>
        </CardHeader>
        <CardContent>
          {soilSample ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="sample-name">Sample name</Label>
                <Input
                  id="sample-name"
                  required
                  value={soilSample?.name ?? ""}
                  disabled={loadState === LoadingState.Loading}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSoilSample({ ...soilSample, name: value });
                    setInput({ ...input, name: value });
                  }}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label htmlFor="soil-moisture">Moisture</Label>
                <Select
                  disabled={loadState === LoadingState.Loading}
                  value={soilSample?.moisture ?? undefined}
                  onValueChange={(value) => {
                    const nextValue = value as MoistureLevel;
                    setSoilSample({ ...soilSample, moisture: nextValue });
                    setInput({ ...input, moisture: nextValue });
                  }}
                >
                  <SelectTrigger id="soil-moisture" className="w-full">
                    <SelectValue placeholder="Select moisture" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={MoistureLevel.Dry}>Dry</SelectItem>
                    <SelectItem value={MoistureLevel.Moist}>Moist</SelectItem>
                    <SelectItem value={MoistureLevel.Wet}>Wet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label htmlFor="soil-season">Season</Label>
                <Select
                  disabled={loadState === LoadingState.Loading}
                  value={soilSample?.season ?? undefined}
                  onValueChange={(value) => {
                    const nextValue = value as Season;
                    setSoilSample({ ...soilSample, season: nextValue });
                    setInput({ ...input, season: nextValue });
                  }}
                >
                  <SelectTrigger id="soil-season" className="w-full">
                    <SelectValue placeholder="Select season" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={Season.Spring}>Spring</SelectItem>
                    <SelectItem value={Season.Summer}>Summer</SelectItem>
                    <SelectItem value={Season.Autumn}>Autumn</SelectItem>
                    <SelectItem value={Season.Winter}>Winter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="sample-date">Sample date</Label>
                <Input
                  id="sample-date"
                  type="date"
                  disabled={loadState === LoadingState.Loading}
                  value={soilSample?.sampleDate ?? ""}
                  onChange={(event) => {
                    const value = event.target.value;
                    const nextSampleDate = value.length > 0 ? value : null;
                    setSoilSample({ ...soilSample, sampleDate: nextSampleDate });
                    setInput({ ...input, sampleDate: nextSampleDate ?? undefined });
                  }}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Soil type</Label>
                <CreatableSelect
                  placeholder="e.g. Sandy loam"
                  options={soilTypes ?? []}
                  loading={!soilTypes || loadState === LoadingState.Loading}
                  value={soilTypes?.find(
                    (st) => st.value === soilSample?.soilType?.id
                  )}
                  onCreateOption={async (val) =>
                    handleCreateOption("SoilType", val)
                  }
                  onChange={(val) => {
                    if (val !== undefined) {
                      setSoilSample({
                        ...soilSample,
                        soilType: { id: val.value },
                      });
                      setInput({ ...input, soilTypeId: val.value });
                    }
                  }}
                  disabled={loadState === LoadingState.Loading}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Crop type</Label>
                <CreatableSelect
                  placeholder="e.g. Arable"
                  options={cropTypes ?? []}
                  loading={!cropTypes || loadState === LoadingState.Loading}
                  disabled={loadState === LoadingState.Loading}
                  onCreateOption={async (val) =>
                    handleCreateOption("CropType", val)
                  }
                  onChange={(val) => {
                    if (val !== undefined) {
                      setSoilSample({
                        ...soilSample,
                        cropType: { id: val.value },
                      });
                      setInput({ ...input, cropTypeId: val.value });
                    }
                  }}
                  value={cropTypes?.find(
                    (ct) => ct.value === soilSample?.cropType?.id
                  )}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Crop (last year)</Label>
                <CreatableSelect
                  placeholder="e.g. Winter wheat"
                  options={crops ?? []}
                  loading={!crops || loadState === LoadingState.Loading}
                  disabled={loadState === LoadingState.Loading}
                  onCreateOption={async (val) =>
                    handleCreateOption("Crop", val)
                  }
                  onChange={(val) => {
                    if (val !== undefined) {
                      setSoilSample({
                        ...soilSample,
                        lastYearCrop: { id: val.value },
                      });
                      setInput({ ...input, lastYearCropId: val.value });
                    }
                  }}
                  value={crops?.find(
                    (c) => c.value === soilSample?.lastYearCrop?.id
                  )}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Crop (year before last)</Label>
                <CreatableSelect
                  placeholder="e.g. Winter barley"
                  options={crops ?? []}
                  loading={!crops || loadState === LoadingState.Loading}
                  disabled={loadState === LoadingState.Loading}
                  onCreateOption={async (val) =>
                    handleCreateOption("Crop", val)
                  }
                  onChange={(val) => {
                    if (val !== undefined) {
                      setSoilSample({
                        ...soilSample,
                        yearBeforeLastCrop: { id: val.value },
                      });
                      setInput({ ...input, yearBeforeLastCropId: val.value });
                    }
                  }}
                  value={crops?.find(
                    (c) => c.value === soilSample?.yearBeforeLastCrop?.id
                  )}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Cultivation</Label>
                <CreatableSelect
                  placeholder="e.g. Direct drill"
                  options={cultivations ?? []}
                  loading={!cultivations || loadState === LoadingState.Loading}
                  disabled={loadState === LoadingState.Loading}
                  onCreateOption={async (val) =>
                    handleCreateOption("Cultivation", val)
                  }
                  onChange={(val) => {
                    if (val !== undefined) {
                      setSoilSample({
                        ...soilSample,
                        cultivation: { id: val.value },
                      });
                      setInput({ ...input, cultivationId: val.value });
                    }
                  }}
                  value={cultivations?.find(
                    (c) => c.value === soilSample?.cultivation?.id
                  )}
                />
              </div>
              <div className="flex w-full flex-col gap-2">
                <Label>Cover crop</Label>
                <CreatableSelect
                  placeholder="e.g. Turnips"
                  options={coverCrops ?? []}
                  loading={!coverCrops || loadState === LoadingState.Loading}
                  disabled={loadState === LoadingState.Loading}
                  value={coverCrops?.find(
                    (cc) => cc.value === soilSample?.coverCrop?.id
                  )}
                  onCreateOption={async (val) =>
                    handleCreateOption("CoverCrop", val)
                  }
                  onChange={(val) => {
                    if (val !== undefined) {
                      setSoilSample({
                        ...soilSample,
                        coverCrop: { id: val.value },
                      });
                      setInput({ ...input, coverCropId: val.value });
                    }
                  }}
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="reference-zone"
                    checked={soilSample?.isReferenceZone ?? false}
                    disabled={loadState === LoadingState.Loading}
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setSoilSample({
                        ...soilSample,
                        isReferenceZone: isChecked,
                      });
                      setInput({ ...input, isReferenceZone: isChecked });
                    }}
                  />
                  <Label htmlFor="reference-zone">
                    This sample is from the reference zone
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="straw-removed"
                    checked={soilSample?.strawRemoved ?? false}
                    disabled={loadState === LoadingState.Loading}
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setSoilSample({
                        ...soilSample,
                        strawRemoved: isChecked,
                      });
                      setInput({ ...input, strawRemoved: isChecked });
                    }}
                  />
                  <Label htmlFor="straw-removed">Straw removed?</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="fym"
                    checked={soilSample?.fym ?? false}
                    disabled={loadState === LoadingState.Loading}
                    onCheckedChange={(checked) => {
                      const isChecked = checked === true;
                      setSoilSample({ ...soilSample, fym: isChecked });
                      setInput({ ...input, fym: isChecked });
                    }}
                  />
                  <Label htmlFor="fym">FYM?</Label>
                </div>
              </div>
            </div>
          ) : (
            <Loading />
          )}
        </CardContent>
      </Card>
      {props.mode === "edit" && (
        <Card>
          <CardHeader>
            <CardTitle>Lab results</CardTitle>
          </CardHeader>
          <CardContent>
            {!soilSample && <Loading />}
            {soilSample && (
              <div
                className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3"
                ref={parent}
              >
                {soilSample?.labResults?.nodes?.map((result) => (
                  <LabResultField
                    key={result.id}
                    disabled={loadState === LoadingState.Loading}
                    labResult={result}
                    soilSampleId={soilSample?.id}
                    laboratories={laboratories ?? []}
                    onCreateLaboratory={(lab) => {
                      setLaboratories([...(laboratories ?? []), lab]);
                      setUpdateLabResultInputs((prev) => {
                        const newInputs = [...prev];
                        const index = newInputs.findIndex(
                          (i) => i.id === result.id
                        );
                        newInputs[index] = {
                          ...newInputs[index],
                          laboratoryId: lab.id,
                        };
                        return newInputs;
                      });
                    }}
                    onChangeUpdateInput={(input) => {
                      setUpdateLabResultInputs((prev) => [
                        ...prev.filter((i) => i.id !== input.id),
                        input,
                      ]);
                    }}
                  />
                ))}
                {/*{notFilled.map((def) => (
                <CreateDefinition
                  indicator={def}
                  key={def.id}
                  disabled={loadState === LoadingState.Loading}
                  onCreateLaboratory={(lab) => {
                    setLaboratories([...(laboratories ?? []), lab]);
                    setCreateLabResultInputs((prev) => [
                      ...prev,
                      {
                        indicatorId: def.id,
                        laboratoryId: lab.id,
                        value: "",
                        soilSampleId: soilSample?.id ?? "",
                      },
                    ]);
                  }}
                  laboratories={laboratories ?? []}
                  onChangeCreateInput={(input) => {
                    setCreateLabResultInputs((prev) => [...prev, input]);
                  }}
                />
              ))}*/}
                {createLabResultInputs.map((input, index) => (
                  <CreateDefinition
                    key={index}
                    indicator={labIndicatorDefinitions.find(
                      (def) => def.id === input.indicatorId
                    )}
                    disabled={loadState === LoadingState.Loading}
                    onCreateLaboratory={(lab) => {
                      setLaboratories([...(laboratories ?? []), lab]);
                      setCreateLabResultInputs((prev) => {
                        const newInputs = [...prev];
                        newInputs[index] = {
                          ...newInputs[index],
                          laboratoryId: lab.id,
                        };
                        return newInputs;
                      });
                      // todo: extract to handler
                    }}
                    laboratories={laboratories ?? []}
                    onChangeCreateInput={(input) => {
                      setCreateLabResultInputs((prev) => {
                        const newInputs = [...prev];
                        newInputs[index] = input;
                        return newInputs;
                      });
                    }}
                  />
                ))}
                {createLabResultInputs.length === 0 ||
                createLabResultInputs[createLabResultInputs.length - 1]
                  .indicatorId !== "" ? (
                  <div className="flex w-full items-center justify-center py-2">
                    <Button
                      type="button"
                      variant="link"
                      disabled={loadState === LoadingState.Loading}
                      onClick={() => {
                        setCreateLabResultInputs([
                          ...createLabResultInputs,
                          {
                            indicatorId: "",
                            laboratoryId: "",
                            value: "",
                            soilSampleId: "",
                          },
                        ]);
                      }}
                    >
                      <PlusIcon className="size-4" />
                      Add result
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </PageTemplate>
  );
};
